import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import {
  Building2,
  MapPin,
  Zap,
  Calendar,
  FileText,
  Eye,
  DollarSign,
  Activity,
  File,
  Download,
  Image as ImageIcon,
  FileType,
  MessageSquare
} from "lucide-react";
import { getSiteDocumentUrl } from '../services/siteService';
import { TenderInboxPanel } from './email/TenderInboxPanel';

interface TenderSite {
  id: string;
  name: string;
  nmi: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  addr_country?: string;
  annual_kwh_peak?: number;
  annual_kwh_offpeak?: number;
  annual_kwh_shoulder?: number;
  abn?: string;
  acn?: string;
  due_at?: string;
  current_contract_expiry?: string;
  current_meter_provider?: string;
  current_retailer?: string;
  dma_provider?: string;
  retailer_comms?: string;
  create_dma?: boolean;
  request_meter_data?: boolean;
  request_bills?: boolean;
  acquire_ced?: boolean;
  document_file_paths?: string[] | null;  // Array of document file paths from site table
  retailer_notes?: string | null;  // Notes for retailers regarding this site
}

interface TenderSitesOverviewProps {
  sites: TenderSite[];
  offers: any[];
  onViewOffers: (site: TenderSite) => void;
  tenderId?: string;
}

const TenderSitesOverview: React.FC<TenderSitesOverviewProps> = ({
  sites,
  offers,
  onViewOffers,
  tenderId
}) => {
  // Helper function to format address
  const formatAddress = (site: TenderSite): string => {
    const parts = [
      site.addr_street,
      site.addr_city,
      site.addr_state,
      site.addr_postcode
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address not specified';
  };

  // Helper function to format annual usage
  const formatAnnualUsage = (site: TenderSite): string => {
    const peak = site.annual_kwh_peak || 0;
    const offpeak = site.annual_kwh_offpeak || 0;
    const shoulder = site.annual_kwh_shoulder || 0;
    const total = peak + offpeak + shoulder;

    if (total === 0) return 'Not specified';
    return `${total.toLocaleString()} kWh/year`;
  };

  // Helper function to get offer count for a site
  const getOfferCount = (siteId: string): number => {
    return offers.filter(offer => offer.tender_site_id === siteId).length;
  };

  // Helper function to get recommended offer count for a site
  const getRecommendedOfferCount = (siteId: string): number => {
    return offers.filter(offer =>
      offer.tender_site_id === siteId && offer.is_recommended
    ).length;
  };

  // Helper function to get file name from path
  const getFileName = (filePath: string): string => {
    return filePath.split('/').pop() || filePath;
  };

  // Helper function to get file extension
  const getFileExtension = (filePath: string): string => {
    const fileName = getFileName(filePath);
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
  };

  // Helper function to get file type icon and color
  const getFileTypeInfo = (fileExt: string): { icon: React.ReactNode; color: string; bgColor: string; label: string } => {
    const ext = fileExt.toLowerCase();

    if (['pdf'].includes(ext)) {
      return {
        icon: <FileText className="w-4 h-4" />,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        label: 'PDF'
      };
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return {
        icon: <ImageIcon className="w-4 h-4" />,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: 'Image'
      };
    }
    if (['doc', 'docx'].includes(ext)) {
      return {
        icon: <FileType className="w-4 h-4" />,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: 'Word'
      };
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return {
        icon: <FileType className="w-4 h-4" />,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        label: 'Excel'
      };
    }
    if (['txt'].includes(ext)) {
      return {
        icon: <FileText className="w-4 h-4" />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        label: 'Text'
      };
    }

    // Default
    return {
      icon: <File className="w-4 h-4" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      label: ext.toUpperCase() || 'File'
    };
  };

  if (sites.length === 0) {
    return (
      <Card className="border-dashed border-2 border-gray-300">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            No Sites Found
          </h4>
          <p className="text-sm text-gray-600 text-center max-w-md">
            This tender doesn't have any sites configured yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Sites Overview
          </h3>
          <p className="text-sm text-gray-600">
            {sites.length} site{sites.length !== 1 ? 's' : ''} in this tender
          </p>
        </div>
      </div>

      <div className="grid gap-4">
        {sites.map((site) => {
          const offerCount = getOfferCount(site.id);
          const recommendedCount = getRecommendedOfferCount(site.id);

          return (
            <Card key={site.id} className="border border-gray-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-2">
                      {site.name || 'Unnamed Site'}
                    </CardTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4" />
                        <span>NMI: {site.nmi || 'Not specified'}</span>
                      </div>
                      {site.abn && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>ABN: {site.abn}</span>
                        </div>
                      )}
                      {site.acn && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>ACN: {site.acn}</span>
                        </div>
                      )}
                      {site.current_meter_provider && (
                        <div className="flex items-center gap-1">
                          <Activity className="w-4 h-4" />
                          <span>Meter: {site.current_meter_provider}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {offerCount} offer{offerCount !== 1 ? 's' : ''}
                    </Badge>
                    {recommendedCount > 0 && (
                      <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                        {recommendedCount} recommended
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Address</p>
                    <p className="text-sm text-gray-600">{formatAddress(site)}</p>
                  </div>
                </div>

                {/* Site Documents */}
                {site.document_file_paths && site.document_file_paths.length > 0 && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-2">Site Documents</p>
                      <div className="flex flex-wrap gap-1.5">
                        {site.document_file_paths.map((filePath, idx) => {
                          const fileName = getFileName(filePath);
                          const fileUrl = getSiteDocumentUrl(filePath);
                          const fileExt = getFileExtension(filePath);
                          const fileTypeInfo = getFileTypeInfo(fileExt);

                          return (
                            <a
                              key={idx}
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 bg-white hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                              title={fileName}
                            >
                              {/* File Type Icon */}
                              <div className={`flex-shrink-0 ${fileTypeInfo.color}`}>
                                {fileTypeInfo.icon}
                              </div>

                              {/* File Name */}
                              <span className="text-xs font-medium text-gray-700 group-hover:text-primary transition-colors truncate max-w-[120px]">
                                {fileName}
                              </span>

                              {/* File Type Badge */}
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
                                {fileTypeInfo.label}
                              </Badge>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Retailer Notes */}
                {site.retailer_notes && (
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-1">Retailer Notes</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-200">{site.retailer_notes}</p>
                    </div>
                  </div>
                )}

                {/* Annual Usage */}
                <div className="flex items-start gap-3">
                  <Activity className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Annual Usage</p>
                    <p className="text-sm text-gray-600">{formatAnnualUsage(site)}</p>
                    {(site.annual_kwh_peak || site.annual_kwh_offpeak || site.annual_kwh_shoulder) && (
                      <div className="text-xs text-gray-500 mt-1">
                        Peak: {site.annual_kwh_peak?.toLocaleString() || 0} |
                        Off-peak: {site.annual_kwh_offpeak?.toLocaleString() || 0} |
                        Shoulder: {site.annual_kwh_shoulder?.toLocaleString() || 0}
                      </div>
                    )}
                  </div>
                </div>

                {/* Current Retailer */}
                {site.current_retailer && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Current Retailer</p>
                      <p className="text-sm text-gray-600">{site.current_retailer}</p>
                    </div>
                  </div>
                )}

                {/* Important Dates */}
                {(site.current_contract_expiry || site.due_at) && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Important Dates</p>
                      <div className="space-y-1">
                        {site.current_contract_expiry && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Contract Expiry:</span> {new Date(site.current_contract_expiry).toLocaleDateString()}
                          </p>
                        )}
                        {site.due_at && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Due Date:</span> {new Date(site.due_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Services */}
                {(site.create_dma || site.request_meter_data || site.request_bills || site.acquire_ced) && (
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-4 h-4 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Requested Services</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {site.create_dma && <Badge variant="outline" className="text-xs">DMA</Badge>}
                        {site.request_meter_data && <Badge variant="outline" className="text-xs">Meter Data</Badge>}
                        {site.request_bills && <Badge variant="outline" className="text-xs">Bills</Badge>}
                        {site.acquire_ced && <Badge variant="outline" className="text-xs">CED</Badge>}
                      </div>
                    </div>
                  </div>
                )}

                {/* View Offers Button */}
                <div className="pt-4 border-t border-gray-100">
                  <Button
                    onClick={() => onViewOffers(site)}
                    className="w-full"
                    variant={offerCount > 0 ? "default" : "outline"}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {offerCount > 0
                      ? `View ${offerCount} Offer${offerCount !== 1 ? 's' : ''}`
                      : 'No Offers Available'
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tender Inbox */}
      {tenderId && (
        <div className="mt-6">
          <TenderInboxPanel tenderId={tenderId} />
        </div>
      )}
    </div>
  );
};

export default TenderSitesOverview;
